import * as sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { useEffect } from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import { getAuthCookies } from './authCookies';

const attachUserFromCookies = (event: sentry.Event): void => {
  if (event.user?.id) return;

  const authCookies = getAuthCookies();
  if (!authCookies) return;

  event.user = { ...event.user, id: authCookies.id };
  event.tags = event.tags || {};
  if (authCookies.type && !event.tags.user_role) {
    event.tags.user_role = authCookies.type;
  }
};

export default {
  init: (dsn: string | undefined) => {
    sentry.init({
      dsn,
      integrations: [
        new BrowserTracing({
          routingInstrumentation: sentry.reactRouterV6Instrumentation(
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes
          ),
        }),
      ],
      tracesSampleRate: 1.0,
      environment: process.env.NODE_ENV,
      beforeSend(event, hint) {
        const error = hint.originalException as any;

        // Filter out axios canceled requests
        if (error && error.message) {
          const errorMessage = error.message.toLowerCase();

          // Common patterns for canceled/aborted requests
          if (
            errorMessage.includes('network error') ||
            errorMessage.includes('request canceled') ||
            errorMessage.includes('request aborted') ||
            errorMessage.includes('canceled') ||
            error.code === 'ERR_CANCELED' ||
            error.code === 'ECONNABORTED'
          ) {
            return null; // Don't send to Sentry
          }
        }

        // Check if it's an AxiosError with canceled status
        if (error && error.isAxiosError && error.code === 'ERR_CANCELED') {
          return null;
        }

        // Try to capture component and query if there is an api issue
        if (error) {
          event.tags = event.tags || {};

          if (!event.tags.component) {
            event.tags.component = window.location.pathname;
          }

          if (error.isAxiosError && error.config) {
            event.tags.api_url = error.config.url;
            event.tags.api_method = error.config.method;
            if (!event.tags.query && error.config.url) {
              try {
                const urlParts = error.config.url.split('?')[0].split('/');
                event.tags.query = urlParts[urlParts.length - 1];
              } catch (e) {
                // ignore
              }
            }
          } else if (
            error.name === 'ApolloError' ||
            error.graphQLErrors ||
            error.networkError
          ) {
            event.tags.api_type = 'graphql';
            const opName =
              error.operationName ||
              error.networkError?.operationName ||
              error.graphQLErrors?.[0]?.operationName;
            if (opName && !event.tags.query) {
              event.tags.query = opName;
            }
          }
        }

        attachUserFromCookies(event);

        return event;
      },

      // Ignore these error messages
      ignoreErrors: [
        'code 401',
        'timeout',
        'code 422',
        'Network Error',
        'Request canceled',
        'Request aborted',
        'canceled',
        'ERR_CANCELED',
        'ECONNABORTED',
        /canceled/i,
        /network error/i,
      ],

      // Ignore URLs from browser extensions
      denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
    });
  },
  identify: (
    id: string,
    role?: string,
    email?: string,
    username?: string,
    businessId?: string
  ) => {
    const user: { id: string; email?: string; username?: string } = { id };
    if (email) user.email = email;
    if (username) user.username = username;
    sentry.setUser(user);

    if (role) sentry.setTag('user_role', role);
    if (businessId) sentry.setTag('business_id', businessId);
  },
  reset: () => {
    sentry.configureScope((scope) => {
      scope.setUser(null);
      scope.setTag('user_role', '');
      scope.setTag('business_id', '');
    });
  },
};
